import {
	prepareCreateBilty,
	prepareCompleteBilty,
	prepareEditBilty,
	prepareMissingKantaBilty,
	prepareCancelBilty,
	prepareLotCreateBilty,
} from '../utils/ModalFunctions'
import { mutateGraphQL } from '../services/MutateGraphQl'
import { combineChildOrders, retryCatch } from '../utils/Functions'
import useMutate from './useMutate'
import { useRecoilValue } from 'recoil'
import { generateSaveBilty } from '../utils/Functions'
import { userAtom } from '../recoil/atoms'

/* ============================================================================================== */
/* ============================= Bilty Management Finish Hooks ================================== */
/* ============================================================================================== */

export const useCreateBilty = () => {
	const user = useRecoilValue(userAtom)
	const matchOrderToJob = useMutate({ segment: 'Jobs', action: 'matchOrderToJob' })
	const createBiltyFunction = async (props) => {
		const {
			props: {
				data: { record },
				segment,
				trackers,
				setSelectedRowKeys,
			},
			values,
		} = props

		const { job, trackerName } = prepareCreateBilty({ user, values, record, segment, trackers })

		return await mutateGraphQL({
			mutationData: job,
			mutateFunction: matchOrderToJob?.mutateFunction,
			mutationInputFunction: matchOrderToJob?.mutationInputFunction,
			mutationOutputFunction: matchOrderToJob?.mutationOutputFunction,
		})
			.catch((error) => {
				console.log(error)
				return { type: 'error' }
			})
			.then((response) => {
				if (response?.JobId === null || response?.type === 'error') {
					return {
						type: 'error',
						message: `Bilty matching was unsuccessful`,
						description: 'Bilty creation has failed. Kindly reach out to the BridgeLinx product team for assistance.',
						response,
					}
				} else {
					if (response?.JobId !== null) {
						if (values?.biltyType === 'systemGenerated') {
							response?.Job?.bilty?.forEach((bilty) => {
								const biltyGenerationData = combineChildOrders({ orders: record?.[bilty?.documentNumber || Object.keys(record)?.[0]], values })
								const body = {
									...values,
									...biltyGenerationData,
									bilty: { _id: bilty?._id, biltyNumber: bilty?.biltyNumber },
									trackerName: trackerName,
								}
								retryCatch({ delay: 500, maxRetries: 3, executionFunction: () => generateSaveBilty({ body }) })
							})
						}
					}
					setSelectedRowKeys([])
					return {
						type: 'success',
						message: `Bilty matched successful`,
						description: `Bilty has been matched successfully with Job # ${response?.JobNumber}`,
						response,
					}
				}
			})
	}
	return [createBiltyFunction, matchOrderToJob?.loading]
}

export const useCreateLotBilty = () => {
	const user = useRecoilValue(userAtom)
	const updateJob = useMutate({ segment: 'Jobs', action: 'updateJob' })
	const updateDriver = useMutate({ segment: 'Drivers', action: 'updateDriver' })
	const updateBilties = useMutate({ segment: 'Bilties', action: 'updateBilties' })
	const createBiltyFunction = async (props) => {
		const {
			props: {
				data: { job, record },
				segment,
				trackers,
			},
			values,
		} = props

		const promises = []
		const { bilties, TrackerId, driver, trackerName } = prepareLotCreateBilty({ user, values, record: job, segment, trackers })

		promises.push(
			mutateGraphQL({
				mutationData: { bilties: bilties },
				mutateFunction: updateBilties?.mutateFunction,
				mutationInputFunction: updateBilties?.mutationInputFunction,
				mutationOutputFunction: updateBilties?.mutationOutputFunction,
			})
		)
		promises.push(
			mutateGraphQL({
				mutationData: { _id: job?._id, TrackerId: TrackerId || null },
				mutateFunction: updateJob?.mutateFunction,
				mutationInputFunction: updateJob?.mutationInputFunction,
				mutationOutputFunction: updateJob?.mutationOutputFunction,
			})
		)
		promises.push(
			mutateGraphQL({
				mutationData: { _id: job?.Driver?._id, cnic: driver?.cnic, cnicDocument: driver?.cnicDocument || null },
				mutateFunction: updateDriver?.mutateFunction,
				mutationInputFunction: updateDriver?.mutationInputFunction,
				mutationOutputFunction: updateDriver?.mutationOutputFunction,
			})
		)

		return Promise.all(promises)
			.catch((error) => {
				console.log(error)
				return { type: 'error' }
			})
			.then((response) => {
				if (response?.type === 'error') {
					return {
						type: 'error',
						message: `Bilty matching was unsuccessful`,
						description: 'Bilty creation has failed. Kindly reach out to the BridgeLinx product team for assistance.',
						response,
					}
				} else {
					if (values?.biltyType === 'systemGenerated') {
						job?.bilty?.forEach((bilty) => {
							const biltyGenerationData = combineChildOrders({ orders: record?.[bilty?.documentNumber || Object.keys(record)?.[0]] })
							const body = {
								...values,
								...biltyGenerationData,
								bilty: { _id: bilty?._id, biltyNumber: bilty?.biltyNumber },
								trackerName: trackerName,
							}
							retryCatch({ delay: 500, maxRetries: 3, executionFunction: () => generateSaveBilty({ body }) })
						})
					}
					return {
						type: 'success',
						message: `Bilty matched successful`,
						description: `Bilty has been matched successfully with Job # ${response?.[1]?.jobNumber}`,
						response: { Job: response?.[1] },
					}
				}
			})
	}
	return [createBiltyFunction, updateBilties?.loading]
}

export const useCompleteBilty = () => {
	const user = useRecoilValue(userAtom)
	const updateBilties = useMutate({ segment: 'Bilties', action: 'updateBilties' })
	const updateTracker = useMutate({ segment: 'Trackers', action: 'updateTracker' })

	const completeBiltyFunction = async (props) => {
		const {
			props: {
				data: { record },
			},
			values,
		} = props

		const bilties = prepareCompleteBilty({ user, values, record })
		const promises = []
		promises.push(
			mutateGraphQL({
				mutationData: { bilties: bilties },
				mutateFunction: updateBilties?.mutateFunction,
				mutationInputFunction: updateBilties?.mutationInputFunction,
				mutationOutputFunction: updateBilties?.mutationOutputFunction,
			})
		)

		if (record?.Tracker?._id && record?.Tracker?.isConnected) {
			promises.push(
				mutateGraphQL({
					mutationData: { _id: record?.Tracker?._id },
					mutationData2: {
						isConnected: false,
					},
					mutateFunction: updateTracker?.mutateFunction,
					mutationInputFunction: updateTracker?.mutationInputFunction,
					mutationInputFunction2: updateTracker?.mutationInputFunction2,
					mutationOutputFunction: updateTracker?.mutationOutputFunction,
				})
			)
		}

		const mutationResult = Promise.all(promises)
			.catch((e) => {
				console.log(e)
				return { type: 'error' }
			})
			.then((response) => {
				if (response?.type === 'error') {
					return {
						type: 'error',
						message: `Bilty completion was unsuccessful`,
						description: 'Bilty completion has failed. Kindly reach out to the BridgeLinx product team for assistance.',
						response,
					}
				} else {
					return { type: 'success', message: `Bilty completion was successful`, response }
				}
			})

		return mutationResult
	}
	return [completeBiltyFunction, updateBilties?.loading || updateTracker?.loading]
}

export const useEditBilty = () => {
	const updateBilties = useMutate({ segment: 'Bilties', action: 'updateBilties' })
	const editBiltyFunction = async (props) => {
		const {
			props: {
				data: { record },
			},
			values,
		} = props

		const bilties = prepareEditBilty({ record, values })
		return mutateGraphQL({
			mutationData: { bilties: bilties },
			mutateFunction: updateBilties?.mutateFunction,
			mutationInputFunction: updateBilties?.mutationInputFunction,
			mutationOutputFunction: updateBilties?.mutationOutputFunction,
		})
			.catch((e) => {
				console.log(e)
				return { type: 'error' }
			})
			.then((response) => {
				if (response?.type === 'error') {
					return {
						type: 'error',
						message: `Bilty updation was unsuccessful`,
						description: 'Bilty updation has failed. Kindly reach out to the BridgeLinx product team for assistance.',
						response,
					}
				} else {
					return { type: 'success', message: `Bilty updation was successful`, response }
				}
			})
	}

	return [editBiltyFunction, updateBilties?.loading]
}

export const useSaveBilty = () => {
	const saveBiltyFunction = async (props) => {
		const {
			props: {
				data: { record },
				segment,
			},
		} = props
		const promises = []
		record?.bilty?.forEach((bilty) => {
			const biltySubOrders = record?.subOrders?.filter((order) => (segment === 'Import' ? order?.documentNumber === bilty?.documentNumber : true))
			const biltyGenerationData = combineChildOrders({ orders: biltySubOrders })
			const body = {
				...biltyGenerationData,
				remarks: bilty?.remarks,
				biltyNumber: bilty?.biltyNumber,
				driverName: record?.Driver?.name,
				driverCNIC: record?.Driver?.cnic,
				trackerName: record?.Tracker?.name,
				driverPhone: record?.Driver?.phoneNumber,
				driverVehicleRegistration: record?.Vehicle?.registrationNumber.toLowerCase(),
				bilty: { _id: bilty?._id, biltyNumber: bilty?.biltyNumber },
			}
			promises.push(retryCatch({ delay: 500, maxRetries: 3, executionFunction: () => generateSaveBilty({ body }) }))
		})
		return await Promise.all(promises)
			.catch((error) => {
				console.log(error)
				return { type: 'error' }
			})
			.then((response) => {
				if (response.type === 'error') {
					return { type: 'error', message: `Bilty document could not be saved for J-${record?.jobNumber}` }
				} else {
					return { type: 'success', message: `Bilty document for J-${record?.jobNumber} was saved successfully`, response }
				}
			})
	}
	return [saveBiltyFunction]
}

export const useCancelBilty = () => {
	const user = useRecoilValue(userAtom)
	const updateBilties = useMutate({ segment: 'Bilties', action: 'updateBilties' })
	const cancelBiltyFunction = async (props) => {
		const {
			props: {
				data: { record },
			},
			values,
		} = props

		const bilties = prepareCancelBilty({ user, record, values })
		return mutateGraphQL({
			mutationData: { bilties },
			mutateFunction: updateBilties?.mutateFunction,
			mutationInputFunction: updateBilties?.mutationInputFunction,
			mutationOutputFunction: updateBilties?.mutationOutputFunction,
		})
			.catch((e) => ({
				type: 'error',
			}))
			.then((response) => {
				if (response?.type === 'error') {
					return {
						type: 'error',
						message: `Bilty cancellation was successfully.`,
						description: 'Bilty cancellation failed. Kindly reach out to the BridgeLinx product team for assistance.',
						response,
					}
				} else {
					return { type: 'success', message: `Bilty cancellation was successful`, response }
				}
			})
	}
	return [cancelBiltyFunction, updateBilties?.loading]
}

export const useMissingKantaInfo = () => {
	const updateBilties = useMutate({ segment: 'Bilties', action: 'updateBilties' })
	const missingKantaFunction = async (props) => {
		const {
			props: {
				data: { record },
			},
			values,
		} = props
		const bilties = prepareMissingKantaBilty({ record, values })
		return mutateGraphQL({
			mutationData: { bilties },
			mutateFunction: updateBilties?.mutateFunction,
			mutationInputFunction: updateBilties?.mutationInputFunction,
			mutationOutputFunction: updateBilties?.mutationOutputFunction,
		})
			.catch((e) => ({ type: 'error' }))
			.then((response) => {
				if (response?.type === 'error') {
					return {
						type: 'error',
						message: `Missing kanta details were not added successfully.`,
						description: 'Failed. Kindly reach out to the BridgeLinx product team for assistance.',
						response,
					}
				} else {
					return { type: 'success', message: `Missing kanta details were added successful`, response }
				}
			})
	}
	return [missingKantaFunction, updateBilties?.loading]
}

export const useMarkOrderComplete = () => {
	const updateStatus = useMutate({ segment: 'Status', action: 'updateStatus' })
	const markOrderCompleteFunction = async (props) => {
		const {
			props: {
				data: { record },
			},
		} = props
		const subOrder = Object.values(record)?.flat()?.[0]

		return mutateGraphQL({
			mutationData: {
				JobOrderId: subOrder?._id,
				type: subOrder?.type,
				status: 'Order Completed',
			},
			mutateFunction: updateStatus?.mutateFunction,
			mutationInputFunction: updateStatus?.mutationInputFunction,
			mutationOutputFunction: updateStatus?.mutationOutputFunction,
		})
			.catch((e) => ({ type: 'error' }))
			.then((response) => {
				if (response?.type === 'error') {
					return {
						type: 'error',
						message: `Order completion failed.`,
						description: 'Failed. Kindly reach out to the BridgeLinx product team for assistance.',
						response,
					}
				} else {
					return { type: 'success', message: `Order ${subOrder?.subOrderNumber} has been marked complete successfully`, response }
				}
			})
	}
	return [markOrderCompleteFunction]
}

/* ============================================================================================== */
/* =============================== Bilty Tracking Finish Hooks ================================== */
/* ============================================================================================== */

export const useMarkTrackingComplete = () => {
	const updateBilties = useMutate({ segment: 'Bilties', action: 'updateBilties' })
	const updateTracker = useMutate({ segment: 'Trackers', action: 'updateTracker' })

	const markTrackingCompleteFunction = async (props) => {
		const {
			props: {
				changedValues,
				data: { record },
			},
		} = props

		const biltyId = Object.keys(changedValues)?.[0]
		const changedBiltyStatus = Object.values(changedValues)?.[0]?.biltyStatus
		const bilties = record?.bilty?.filter((bilty) => bilty?._id === biltyId)?.map((bilty) => ({ _id: bilty?._id, biltyStatus: changedBiltyStatus }))
		const promises = []
		promises?.push(
			mutateGraphQL({
				mutationData: { bilties },
				mutateFunction: updateBilties?.mutateFunction,
				mutationInputFunction: updateBilties?.mutationInputFunction,
				mutationOutputFunction: updateBilties?.mutationOutputFunction,
			})
		)
		if (record?.Tracker?._id && record?.Tracker?.isConnected) {
			promises.push(
				mutateGraphQL({
					mutationData: { _id: record?.Tracker?._id },
					mutationData2: {
						isConnected: false,
					},
					mutateFunction: updateTracker?.mutateFunction,
					mutationInputFunction: updateTracker?.mutationInputFunction,
					mutationInputFunction2: updateTracker?.mutationInputFunction2,
					mutationOutputFunction: updateTracker?.mutationOutputFunction,
				})
			)
		}
		return await Promise.all(promises)
			.catch((error) => {
				console.log(error)
				return { type: 'error' }
			})
			.then((response) => {
				if (response.type === 'error') {
					return { type: 'error', message: `Bilty tracking status change for J-${record?.jobNumber} was unsuccessful` }
				} else {
					return { type: 'success', message: `Bilty tracking for J-${record?.jobNumber} was marked successfully`, response }
				}
			})
	}
	return [markTrackingCompleteFunction, updateBilties?.loading || updateTracker?.loading]
}

/* ============================================================================================== */
/* ============================== User Management Finish Hooks ================================== */
/* ============================================================================================== */

export const useAssignVicinities = () => {
	const updateBiltyOps = useMutate({ segment: 'BiltyOps', action: 'updateBiltyOps' })
	const swapUserUserTypes = useMutate({ segment: 'UserTypes', action: 'swapUserTypes' })
	const updateUserUserType = useMutate({ segment: 'UserTypes', action: 'updateUserTypes' })
	const deleteBiltyRegions = useMutate({ segment: 'BiltyOps', action: 'deleteBiltyRegions' })
	const insertBiltyRegions = useMutate({ segment: 'BiltyOps', action: 'insertBiltyRegions' })

	const assignVicinitiesFunction = async (props) => {
		const {
			values,
			props: {
				biltyUserTypeId,
				data: { record },
			},
		} = props

		const existingUserTypeId = record?.biltyUserTypeId
		const existingRegions = record?.regions?.map((region) => region?.value)
		const insertRegions = values?.assignRegions?.filter((region) => !existingRegions?.includes(region)) || []
		const deleteRegions = existingRegions?.filter((region) => !values?.assignRegions?.includes(region))

		const promises = []
		if (!existingUserTypeId) {
			promises.push(
				mutateGraphQL({
					mutationData: {
						_id: record?.userId,
						UserUserTypes: { UserType: biltyUserTypeId },
					},
					mutateFunction: updateUserUserType?.mutateFunction,
					mutationInputFunction: updateUserUserType?.mutationInputFunction,
					mutationOutputFunction: updateUserUserType?.mutationOutputFunction,
				})
			)
		} else {
			promises.push(
				mutateGraphQL({
					mutationData: {
						_id: record?.userId,
						toSwapId: existingUserTypeId,
						withSwapId: biltyUserTypeId,
					},
					mutateFunction: swapUserUserTypes?.mutateFunction,
					mutationInputFunction: swapUserUserTypes?.mutationInputFunction,
					mutationOutputFunction: swapUserUserTypes?.mutationOutputFunction,
				})
			)
		}

		promises.push(
			mutateGraphQL({
				mutationData: {
					_id: record?._id,
					isBlocked: false,
				},
				mutateFunction: updateBiltyOps?.mutateFunction,
				mutationInputFunction: updateBiltyOps?.mutationInputFunction,
				mutationOutputFunction: updateBiltyOps?.mutationOutputFunction,
			})
		)
		if (deleteRegions?.length !== 0) {
			promises.push(
				mutateGraphQL({
					mutationData: {
						BiltyOpsId: record?._id,
						RegionId: deleteRegions,
					},
					mutateFunction: deleteBiltyRegions?.mutateFunction,
					mutationInputFunction: deleteBiltyRegions?.mutationInputFunction,
					mutationOutputFunction: deleteBiltyRegions?.mutationOutputFunction,
				})
			)
		}
		if (insertRegions?.length !== 0) {
			promises.push(
				mutateGraphQL({
					mutationData: {
						BiltyOpsId: record?._id,
						RegionId: insertRegions,
					},
					mutateFunction: insertBiltyRegions?.mutateFunction,
					mutationInputFunction: insertBiltyRegions?.mutationInputFunction,
					mutationOutputFunction: insertBiltyRegions?.mutationOutputFunction,
				})
			)
		}

		return await Promise.all(promises)
			.catch((error) => {
				console.log(error)
				return { type: 'error' }
			})
			.then((response) => {
				if (response.type === 'error') {
					return { type: 'error', message: `Bilty user update was unsuccessful` }
				} else {
					return { type: 'success', message: `Bilty user was updated successfully`, response }
				}
			})
	}
	return [
		assignVicinitiesFunction,
		updateBiltyOps?.loading || deleteBiltyRegions?.loading || updateUserUserType?.loading || swapUserUserTypes?.loading || insertBiltyRegions?.loading,
	]
}

/* ============================================================================================== */
/* =========================== Tracker Management Finish Hooks ================================== */
/* ============================================================================================== */

export const useCreateTracker = () => {
	const createTracker = useMutate({ segment: 'Trackers', action: 'createTracker' })
	const createTrackerFunction = async (props) => {
		const { values } = props
		const variables = {
			...values,
			isConnected: false,
		}
		return await mutateGraphQL({
			mutationData: variables,
			mutateFunction: createTracker?.mutateFunction,
			mutationInputFunction: createTracker?.mutationInputFunction,
			mutationOutputFunction: createTracker?.mutationOutputFunction,
		})
			.catch((error) => {
				console.log(error)
				return { type: 'error' }
			})
			.then((response) => {
				const trackerName = response?.trackers?.[0]?.name
				if (response.type === 'error') {
					return { type: 'error', message: 'Error', description: 'Unable to Create Tracker' }
				} else {
					return { type: 'success', message: 'Tracker Added', description: `${trackerName} was created Successfully`, response }
				}
			})
	}
	return [createTrackerFunction, createTracker?.loading]
}

export const useDisconnetTracker = () => {
	const updateTracker = useMutate({ segment: 'Trackers', action: 'updateTracker' })
	const disconnectTrackerfunction = async (props) => {
		const {
			props: {
				data: { record },
			},
		} = props
		return await mutateGraphQL({
			mutationData: { _id: record?._id },
			mutationData2: {
				isConnected: !record?.isConnected,
			},
			mutateFunction: updateTracker?.mutateFunction,
			mutationInputFunction: updateTracker?.mutationInputFunction,
			mutationInputFunction2: updateTracker?.mutationInputFunction2,
			mutationOutputFunction: updateTracker?.mutationOutputFunction,
		})
			.catch((error) => {
				console.log(error)
				return { type: 'error' }
			})
			.then((response) => {
				const trackerName = response?.trackers?.[0]?.name
				if (response.type === 'error') {
					return { type: 'error', message: 'Error', description: 'Unable to disconnect tracker' }
				} else {
					return {
						type: 'success',
						message: 'Tracker Added',
						description: `${trackerName} was ${!record?.isConnected ? 'connected' : 'disconnected'} successfully`,
						response,
					}
				}
			})
	}
	return [disconnectTrackerfunction, updateTracker?.loading]
}

export const useChangeTrackerStatus = () => {
	const updateTracker = useMutate({ segment: 'Trackers', action: 'updateTracker' })
	const changeTrackerStatusFunction = async (props) => {
		const {
			props: {
				data: { record },
			},
			status,
		} = props
		return await mutateGraphQL({
			mutationData: { _id: record?._id },
			mutationData2: {
				status: status,
			},
			mutateFunction: updateTracker?.mutateFunction,
			mutationInputFunction: updateTracker?.mutationInputFunction,
			mutationInputFunction2: updateTracker?.mutationInputFunction2,
			mutationOutputFunction: updateTracker?.mutationOutputFunction,
		})
			.catch((error) => {
				console.log(error)
				return { type: 'error' }
			})
			.then((response) => {
				const trackerName = response?.trackers?.[0]?.name
				if (response.type === 'error') {
					return { type: 'error', message: 'Error', description: 'Unable to change tracker status' }
				} else {
					return { type: 'success', message: 'Tracker Added', description: `${trackerName} status was updated successfully`, response }
				}
			})
	}
	return [changeTrackerStatusFunction, updateTracker?.loading]
}
